import {
    isAdditionalInformationQuestionsExistInForm,
    isEquipmentBased,
    isExperienceProduct,
    isMembershipProduct,
    isPackageProduct,
    isPartnerPackageProduct,
} from "common";
import React from "react";
import { Product } from "types";
import { GuestQuestionnaireList } from "./GuestQuestionnaire/GuestQuestionnaireList";
import { MemberIdentificationInfoAlert } from "./Membership/MemberIdentificationInfoAlert";
import { OrganizerQuestionnaireList } from "./OrganizerQuestionnaire/OrganizerQuestionnaireList";
import { hasUniqueGuestIdentification } from "@/models/Experience";
import { useCreatePurchaseStore } from "@/stores/createPurchaseStore";

interface CheckoutGuestQuestionnaireProps {
    product: Product;
}
export const CheckoutGuestQuestionnaire = React.memo(({ product }: CheckoutGuestQuestionnaireProps) => {
    const form = useCreatePurchaseStore((state) => state.getForm());
    const isInitialized = useCreatePurchaseStore((state) => state.purchaseId);
    const isOneStepCheckout = useCreatePurchaseStore((state) => state.isOneStepCheckout());
    const activeItemGuests = useCreatePurchaseStore((state) => state.getActiveItem()?.guests);

    const isMembership = isMembershipProduct(product);
    const isPackage = isPackageProduct(product) || isPartnerPackageProduct(product);
    const isAttendeeUnique = isExperienceProduct(product) ? hasUniqueGuestIdentification(product) : true;

    const hasOrganizerQuestions = isAdditionalInformationQuestionsExistInForm(form);
    // Equipment experiences with "Collect demographic information" off have no attendee guests and are
    // treated as organizer-level (!isAttendeeUnique). Their questionnaire is collected at the item level,
    // so don't require attendee guests to show it — otherwise only require guests for the per-attendee case.
    const hasAttendeesOrIsOrganizerLevel =
        !isExperienceProduct(product) ||
        !isEquipmentBased(product) ||
        !isAttendeeUnique ||
        (activeItemGuests ?? []).length > 0;
    const showAttendeeInfo = (isOneStepCheckout || isPackage) && hasAttendeesOrIsOrganizerLevel;

    if (!isInitialized || !showAttendeeInfo) {
        return null;
    }

    return (
        <>
            {hasOrganizerQuestions && isAttendeeUnique && (
                <OrganizerQuestionnaireList isAttendeeUnique label="Additional Information" />
            )}

            {isAttendeeUnique ? <GuestQuestionnaireList /> : <OrganizerQuestionnaireList />}

            {isMembership && <MemberIdentificationInfoAlert membership={product} />}
        </>
    );
});
